import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AlertTriangle, BookOpen, CheckCircle2, FileText, Shield, Users } from 'lucide-react';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { requirementSource, useComplianceCoverage } from '../../../../../hooks/useComplianceCoverage';
import { useProjectVolumes } from '../../../../../hooks/useProjectVolumes';
import { useSolicitationInsights } from '../../../../../hooks/useSolicitationInsights';
import { groupByLogicalKey } from '../../../v2/amendment/groupByLogicalKey';
import { ComplianceCoverageRing, RingLegend } from './ComplianceCoverageRing';
import { DeadlineTimeline } from './DeadlineTimeline';
/**
 * OverviewView — proposal health dashboard for capture/proposal managers.
 *
 * Answers: Are we compliant? What's the deadline? What's the volume status?
 * What are the blockers? What evaluation factors matter?
 */ export function OverviewView({ pipeline, projectId }) {
    const matrix = useComplianceCoverage(projectId ?? '');
    const volumes = useProjectVolumes(projectId);
    const currentMandatory = !matrix.isError && matrix.isFetchedAfterMount ? matrix.data?.items.filter((item)=>!item.superseded && item.versionId === matrix.data.currentVersionId && requirementSource(item).IsMandatory) : undefined;
    const matrixStats = currentMandatory && {
        total: currentMandatory.length,
        addressed: currentMandatory.filter((item)=>item.substantiveState === 'addressed').length,
        partial: currentMandatory.filter((item)=>item.substantiveState === 'partial').length,
        notAddressed: currentMandatory.filter((item)=>item.substantiveState === 'unaddressed').length
    };
    const deadlines = useMemo(()=>{
        const raw = pipeline.deadlines ?? [];
        return groupByLogicalKey(raw).currentList;
    }, [
        pipeline.deadlines
    ]);
    const keyPersonnel = useMemo(()=>{
        const raw = pipeline.keyPersonnel ?? [];
        return groupByLogicalKey(raw).currentList;
    }, [
        pipeline.keyPersonnel
    ]);
    const securityClearances = useMemo(()=>{
        const raw = pipeline.securityClearances ?? [];
        return groupByLogicalKey(raw).currentList;
    }, [
        pipeline.securityClearances
    ]);
    const evalScheme = useSolicitationInsights(projectId ?? '').data?.evaluationScheme;
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-5",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "grid grid-cols-1 gap-4 lg:grid-cols-5",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "animate-fade-in rounded-xl border border-slate-200 bg-white p-5 lg:col-span-2",
                        children: [
                            /*#__PURE__*/ _jsx("h3", {
                                className: "mb-4 text-xs font-semibold uppercase tracking-wider text-slate-400",
                                children: "Reviewed mandatory coverage"
                            }),
                            matrixStats ? /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center gap-6",
                                children: [
                                    /*#__PURE__*/ _jsx(ComplianceCoverageRing, {
                                        addressed: matrixStats.addressed,
                                        partial: matrixStats.partial,
                                        notAddressed: matrixStats.notAddressed,
                                        total: matrixStats.total,
                                        size: 100
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex-1 space-y-2.5",
                                        children: [
                                            /*#__PURE__*/ _jsx(RingLegend, {
                                                items: [
                                                    {
                                                        color: '#10b981',
                                                        label: 'Addressed',
                                                        count: matrixStats.addressed
                                                    },
                                                    {
                                                        color: '#f59e0b',
                                                        label: 'Partial',
                                                        count: matrixStats.partial
                                                    },
                                                    {
                                                        color: '#f43f5e',
                                                        label: 'Not addressed',
                                                        count: matrixStats.notAddressed
                                                    }
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "text-xs text-slate-400",
                                                children: [
                                                    matrixStats.total,
                                                    " current mandatory requirements"
                                                ]
                                            })
                                        ]
                                    })
                                ]
                            }) : /*#__PURE__*/ _jsx("div", {
                                className: "flex h-24 items-center justify-center text-xs text-slate-400",
                                children: "Issue a solicitation and review its requirements to confirm coverage."
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "animate-fade-in rounded-xl border border-slate-200 bg-white p-5 lg:col-span-3",
                        style: {
                            animationDelay: '50ms'
                        },
                        children: [
                            /*#__PURE__*/ _jsx("h3", {
                                className: "mb-4 text-xs font-semibold uppercase tracking-wider text-slate-400",
                                children: "Key Deadlines"
                            }),
                            deadlines.length > 0 ? /*#__PURE__*/ _jsx(DeadlineTimeline, {
                                deadlines: deadlines.map((d)=>({
                                        type: d.type || '',
                                        date: d.date || '',
                                        time: d.time,
                                        timezone: d.timezone
                                    }))
                            }) : /*#__PURE__*/ _jsx("div", {
                                className: "flex h-24 items-center justify-center text-xs text-slate-400",
                                children: "No deadlines extracted yet."
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "grid grid-cols-1 gap-4 lg:grid-cols-2",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "rounded-xl border border-slate-200 bg-white p-5",
                        children: [
                            /*#__PURE__*/ _jsxs("h3", {
                                className: "mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400",
                                children: [
                                    "Volumes (",
                                    (volumes.data ?? []).length,
                                    ")"
                                ]
                            }),
                            (volumes.data ?? []).length > 0 ? /*#__PURE__*/ _jsx("div", {
                                className: "space-y-2",
                                children: (volumes.data ?? []).map((v)=>/*#__PURE__*/ _jsxs(Link, {
                                        to: `/proposal/${projectId}/v2/volume/${v.volumeId}`,
                                        className: "flex items-center gap-3 rounded-lg border border-slate-100 px-3 py-2.5 transition-colors hover:border-slate-200 hover:bg-slate-50",
                                        children: [
                                            /*#__PURE__*/ _jsx(BookOpen, {
                                                className: "h-4 w-4 flex-shrink-0 text-blue-500"
                                            }),
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "min-w-0 flex-1",
                                                children: /*#__PURE__*/ _jsx("div", {
                                                    className: "truncate text-[13px] font-medium text-slate-800",
                                                    children: v.displayName
                                                })
                                            }),
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "flex items-center gap-1.5",
                                                children: [
                                                    v.fileFormat && /*#__PURE__*/ _jsx("span", {
                                                        className: "rounded border border-slate-200 bg-slate-50 px-1.5 py-px text-[10px] font-medium text-slate-500",
                                                        children: v.fileFormat.toUpperCase()
                                                    }),
                                                    v.requiredOptional === 'required' && /*#__PURE__*/ _jsx("span", {
                                                        className: "rounded border border-emerald-200 bg-emerald-50 px-1.5 py-px text-[10px] font-medium text-emerald-600",
                                                        children: "Required"
                                                    })
                                                ]
                                            })
                                        ]
                                    }, v.volumeId))
                            }) : /*#__PURE__*/ _jsx("div", {
                                className: "flex h-20 items-center justify-center text-xs text-slate-400",
                                children: "No volumes identified yet."
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "rounded-xl border border-slate-200 bg-white p-5",
                        children: [
                            /*#__PURE__*/ _jsx("h3", {
                                className: "mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400",
                                children: "Critical Items"
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "space-y-2",
                                children: [
                                    /*#__PURE__*/ _jsx(CriticalItem, {
                                        icon: /*#__PURE__*/ _jsx(Users, {
                                            className: "h-4 w-4"
                                        }),
                                        label: "Key Personnel",
                                        value: `${keyPersonnel.length} role${keyPersonnel.length === 1 ? '' : 's'} identified`,
                                        status: keyPersonnel.length > 0 ? 'ok' : 'warning'
                                    }),
                                    /*#__PURE__*/ _jsx(CriticalItem, {
                                        icon: /*#__PURE__*/ _jsx(Shield, {
                                            className: "h-4 w-4"
                                        }),
                                        label: "Security Clearance",
                                        value: securityClearances.length > 0 ? securityClearances[0].level || 'Required' : 'None identified',
                                        status: securityClearances.length > 0 ? 'ok' : 'neutral'
                                    }),
                                    /*#__PURE__*/ _jsx(CriticalItem, {
                                        icon: /*#__PURE__*/ _jsx(FileText, {
                                            className: "h-4 w-4"
                                        }),
                                        label: "Evaluation Method",
                                        value: evalScheme?.method?.replace(/_/g, ' ') || 'Not extracted',
                                        status: evalScheme ? 'ok' : 'warning'
                                    }),
                                    evalScheme && evalScheme.factors.length > 0 && /*#__PURE__*/ _jsxs("div", {
                                        className: "mt-3 rounded-lg border border-slate-100 bg-slate-50/50 p-3",
                                        children: [
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "mb-2 text-[10px] font-bold uppercase tracking-wider text-slate-400",
                                                children: "Evaluation Factors"
                                            }),
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "space-y-1.5",
                                                children: [
                                                    evalScheme.factors.slice(0, 5).map((f, i)=>/*#__PURE__*/ _jsxs("div", {
                                                            className: "flex items-baseline justify-between gap-2 text-[12px]",
                                                            children: [
                                                                /*#__PURE__*/ _jsx("span", {
                                                                    className: "min-w-0 truncate text-slate-700",
                                                                    children: f.name
                                                                }),
                                                                f.weight && /*#__PURE__*/ _jsx("span", {
                                                                    className: "flex-shrink-0 text-[11px] text-slate-500",
                                                                    children: f.weight
                                                                })
                                                            ]
                                                        }, i)),
                                                    evalScheme.factors.length > 5 && /*#__PURE__*/ _jsxs("div", {
                                                        className: "text-[11px] text-slate-400",
                                                        children: [
                                                            "+",
                                                            evalScheme.factors.length - 5,
                                                            " more"
                                                        ]
                                                    })
                                                ]
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    })
                ]
            }),
            pipeline.extractorVersion && /*#__PURE__*/ _jsxs("div", {
                className: "text-[11px] text-slate-400",
                children: [
                    "Pipeline: ",
                    /*#__PURE__*/ _jsx("span", {
                        className: "font-mono",
                        children: pipeline.extractorVersion
                    })
                ]
            })
        ]
    });
}
function CriticalItem({ icon, label, value, status }) {
    const statusConfig = {
        ok: {
            dot: 'bg-emerald-500',
            Icon: CheckCircle2,
            color: 'text-emerald-600'
        },
        warning: {
            dot: 'bg-amber-500',
            Icon: AlertTriangle,
            color: 'text-amber-600'
        },
        critical: {
            dot: 'bg-rose-500',
            Icon: AlertTriangle,
            color: 'text-rose-600'
        },
        neutral: {
            dot: 'bg-slate-300',
            Icon: CheckCircle2,
            color: 'text-slate-400'
        }
    };
    const cfg = statusConfig[status];
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex items-center gap-3 rounded-lg border border-slate-100 px-3 py-2.5",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "text-slate-400",
                children: icon
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "min-w-0 flex-1",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "text-[11px] font-medium uppercase tracking-wide text-slate-400",
                        children: label
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "text-[13px] font-medium text-slate-800",
                        children: value
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: `h-2.5 w-2.5 rounded-full ${cfg.dot}`
            })
        ]
    });
}
